import type {
  BridgeChartBar,
  BridgeChartIncrementalRejectionCode,
  BridgeChartIncrementalUpdate,
  BridgeChartIncrementalUpdateResult,
  BridgeChartInput,
  BridgeChartMarker,
  BridgeChartMetadata,
  BridgeChartComparisonSeries,
  BridgeChartSchemaVersion,
  NormalizedBridgeChartData
} from "../types/data";
import { normalizeStudySeries } from "../studies/studyCatalog";
import { normalizeIntelligenceMarkers } from "../overlays/markers";

const schemaVersion: BridgeChartSchemaVersion = "1.0";
const defaultMaxRetainedBars = 10_000;
const defaultRevisionWindowBars = 8;
const maximumIncrementalBars = 512;

export function parseChartData(input: BridgeChartInput = {}): NormalizedBridgeChartData {
  const metadata: BridgeChartMetadata = {
    symbol: String(input.symbol || input.metadata?.symbol || ""),
    timeframe: String(input.timeframe || input.metadata?.timeframe || "1D"),
    ...input.metadata
  };
  const bars = normalizeBars(input.bars || []);
  const dateIndex: Record<string, number> = {};
  bars.forEach((bar, index) => { dateIndex[bar.date] = index; });

  const indicators = normalizeStudySeries(input.indicators || []);

  const strategyMarkers = normalizeMarkers(input.strategyMarkers || input.strategySignals || []);
  const tradeIdeaMarkers = normalizeMarkers(input.tradeIdeaMarkers || []);
  const comparisonSeries = normalizeComparisonSeries(input.comparisonSeries || [], dateIndex);
  const evidenceByKey = Object.fromEntries((input.evidenceTooltips || []).map(evidence => [
    evidenceKey(evidence.strategy, evidence.direction),
    {
      ...evidence,
      sampleSize: Number(evidence.sampleSize || 0),
      winRate: Number(evidence.winRate || 0),
      averageReturn: Number(evidence.averageReturn || 0),
      maxDrawdown: Number(evidence.maxDrawdown || 0)
    }
  ]));

  return {
    schemaVersion,
    symbol: metadata.symbol,
    timeframe: metadata.timeframe,
    metadata: {
      ...metadata,
      symbol: metadata.symbol,
      timeframe: metadata.timeframe,
      barCount: metadata.barCount ?? bars.length,
      hasBars: metadata.hasBars ?? bars.length > 0,
      hasIndicators: metadata.hasIndicators ?? indicators.some(x => x.points.length > 0),
      hasStrategySignals: metadata.hasStrategySignals ?? strategyMarkers.length > 0,
      hasEvidence: metadata.hasEvidence ?? Object.keys(evidenceByKey).length > 0
    },
    bars,
    dateIndex,
    indicators,
    strategyMarkers,
    tradeIdeaMarkers,
    intelligenceMarkers: normalizeIntelligenceMarkers(input.intelligenceMarkers || []),
    markerAvailability: input.markerAvailability || [],
    evidenceTooltips: input.evidenceTooltips || [],
    evidenceByKey,
    comparisonSeries,
    warnings: input.warnings || []
  };
}

export function normalizeComparisonSeries(
  series: readonly BridgeChartComparisonSeries[],
  primaryDateIndex: Readonly<Record<string, number>>
): NormalizedBridgeChartData["comparisonSeries"] {
  if (series.length > 3) throw new Error("BridgeChartComparisonSeriesLimitExceeded");
  const identities = new Set<string>();
  return series.map(item => {
    const seriesId = item.seriesId.trim();
    const symbol = item.symbol.trim();
    const sourceId = item.sourceId.trim();
    const sourceRevision = item.sourceRevision.trim();
    if (!seriesId || !symbol || !sourceId || !sourceRevision || identities.has(seriesId)) {
      throw new Error("BridgeChartComparisonSeriesIdentityInvalid");
    }
    identities.add(seriesId);
    const pointsByDate: Record<string, number> = {};
    let previousDate = "";
    for (const point of item.points) {
      const date = String(point.date);
      const value = Number(point.value);
      if (!(date in primaryDateIndex) || date <= previousDate || !Number.isFinite(value)) {
        throw new Error("BridgeChartComparisonSeriesPointInvalid");
      }
      previousDate = date;
      pointsByDate[date] = value;
    }
    return {
      seriesId,
      symbol,
      sourceId,
      sourceRevision,
      points: Object.entries(pointsByDate).map(([date, value]) => ({ date, value })),
      pointsByDate,
      scaleOwnership: "Independent" as const
    };
  });
}

export function evidenceKey(strategy: string, direction: string): string {
  return `${normalizeKey(strategy)}|${normalizeKey(direction)}`;
}

export function reconcileIncrementalBars(
  current: readonly BridgeChartBar[],
  update: BridgeChartIncrementalUpdate
): { bars: BridgeChartBar[]; result: BridgeChartIncrementalUpdateResult } {
  const mode = update.mode;
  const incoming = update.bars.map(cloneBar);
  const currentLast = current.length > 0 ? current[current.length - 1].date : null;
  if (incoming.length === 0) return rejected(mode, current, "EmptyUpdate", null);
  if (incoming.length > maximumIncrementalBars) return rejected(mode, current, "UpdateLimitExceeded", null);
  if (update.expectedLastDate !== undefined && update.expectedLastDate !== currentLast) {
    return rejected(mode, current, "StaleBase", update.expectedLastDate);
  }

  const seen = new Map<string, BridgeChartBar>();
  for (const bar of incoming) {
    if (!isValidCandle(bar)) return rejected(mode, current, "MalformedCandle", bar.date);
    const duplicate = seen.get(bar.date);
    if (duplicate) {
      return rejected(mode, current, sameBar(duplicate, bar) ? "DuplicateCandle" : "ConflictingCandle", bar.date);
    }
    seen.set(bar.date, bar);
  }
  for (let index = 1; index < incoming.length; index++) {
    if (incoming[index - 1].date >= incoming[index].date) {
      return rejected(mode, current, "CandleOrderInvalid", incoming[index].date);
    }
  }

  const currentIndex = new Map(current.map((bar, index) => [bar.date, index]));
  let appended = 0;
  let revised = 0;
  let next = current.map(cloneBar);
  if (mode === "append") {
    for (const bar of incoming) {
      const existingIndex = currentIndex.get(bar.date);
      if (existingIndex !== undefined) {
        return rejected(mode, current, sameBar(current[existingIndex], bar) ? "DuplicateCandle" : "ConflictingCandle", bar.date);
      }
      if (currentLast !== null && bar.date <= currentLast) return rejected(mode, current, "StaleCandle", bar.date);
    }
    next.push(...incoming);
    appended = incoming.length;
  } else if (mode === "revision") {
    const revisionWindow = boundedInteger(update.revisionWindowBars, defaultRevisionWindowBars, 1, 512);
    const earliestRevisionIndex = Math.max(0, current.length - revisionWindow);
    for (const bar of incoming) {
      const existingIndex = currentIndex.get(bar.date);
      if (existingIndex === undefined) return rejected(mode, current, "UnknownRevisionCandle", bar.date);
      if (existingIndex < earliestRevisionIndex) return rejected(mode, current, "StaleCandle", bar.date);
      if (sameBar(current[existingIndex], bar)) return rejected(mode, current, "DuplicateCandle", bar.date);
      next[existingIndex] = bar;
      revised++;
    }
  } else {
    return rejected(mode, current, "MalformedCandle", null);
  }

  const maxRetained = boundedInteger(update.maxRetainedBars, defaultMaxRetainedBars, 64, 100_000);
  if (next.length > maxRetained) next = next.slice(next.length - maxRetained);
  return {
    bars: next,
    result: {
      status: "Applied",
      mode,
      appended,
      revised,
      retained: next.length,
      firstDate: next[0]?.date ?? null,
      lastDate: next.length > 0 ? next[next.length - 1].date : null,
      rejectionCode: null,
      rejectedDate: null
    }
  };
}

function normalizeBars(bars: BridgeChartBar[]): BridgeChartBar[] {
  const normalized = bars.map(cloneBar).sort((a, b) => a.date.localeCompare(b.date));
  for (let index = 0; index < normalized.length; index++) {
    const bar = normalized[index];
    if (!isValidCandle(bar)) throw new Error("BridgeChartMalformedCandle");
    if (index > 0 && normalized[index - 1].date === bar.date) {
      throw new Error(sameBar(normalized[index - 1], bar)
        ? "BridgeChartDuplicateCandle" : "BridgeChartConflictingCandle");
    }
  }
  return normalized;
}

function rejected(
  mode: BridgeChartIncrementalUpdate["mode"],
  current: readonly BridgeChartBar[],
  rejectionCode: BridgeChartIncrementalRejectionCode,
  rejectedDate: string | null
): { bars: BridgeChartBar[]; result: BridgeChartIncrementalUpdateResult } {
  const bars = current.map(cloneBar);
  return {
    bars,
    result: {
      status: "Rejected",
      mode,
      appended: 0,
      revised: 0,
      retained: bars.length,
      firstDate: bars[0]?.date ?? null,
      lastDate: bars.length > 0 ? bars[bars.length - 1].date : null,
      rejectionCode,
      rejectedDate
    }
  };
}

function cloneBar(bar: BridgeChartBar): BridgeChartBar {
  return {
    date: String(bar.date),
    open: Number(bar.open),
    high: Number(bar.high),
    low: Number(bar.low),
    close: Number(bar.close),
    volume: Number(bar.volume)
  };
}

function sameBar(left: BridgeChartBar, right: BridgeChartBar): boolean {
  return left.date === right.date && left.open === right.open && left.high === right.high &&
    left.low === right.low && left.close === right.close && left.volume === right.volume;
}

function isValidCandle(bar: BridgeChartBar): boolean {
  if (!isCanonicalDate(bar.date)) return false;
  if (![bar.open, bar.high, bar.low, bar.close, bar.volume].every(Number.isFinite)) return false;
  if (bar.volume < 0 || bar.high < bar.low) return false;
  return bar.high >= Math.max(bar.open, bar.close) && bar.low <= Math.min(bar.open, bar.close);
}

function isCanonicalDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}Z)?$/.test(value)) return false;
  const day = value.slice(0, 10);
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isFinite(parsed.valueOf()) && parsed.toISOString().slice(0, 10) === day;
}

function boundedInteger(value: number | undefined, fallback: number, minimum: number, maximum: number): number {
  if (value === undefined) return fallback;
  if (!Number.isInteger(value) || value < minimum || value > maximum) return fallback;
  return value;
}

function normalizeMarkers(markers: BridgeChartMarker[]): BridgeChartMarker[] {
  return markers.map(marker => ({
    date: String(marker.date),
    strategy: String(marker.strategy || ""),
    direction: String(marker.direction || ""),
    score: Number(marker.score || 0),
    confidence: Number(marker.confidence || 0),
    explanation: marker.explanation ?? null,
    evidenceBacked: Boolean(marker.evidenceBacked),
    label: marker.label
  })).filter(marker => marker.date.length > 0);
}

function normalizeKey(value: string): string {
  return String(value || "").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}
